import React from 'react';
import { Loader } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button, ButtonProps } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { requestStatusType } from '../../types';

// Map MUI variants to shadcn variants
const mapVariant = (muiVariant?: string): ButtonProps['variant'] => {
  switch (muiVariant) {
    case 'contained':
      return 'default';
    case 'outlined':
    case 'outline':
      return 'outline';
    case 'text':
      return 'ghost';
    default:
      return muiVariant as ButtonProps['variant'];
  }
};

// Map MUI color to shadcn variant modifications
const mapColorToVariant = (
  color?: string,
  variant?: string,
): ButtonProps['variant'] => {
  if (color === 'error' && (variant === 'contained' || variant === 'default')) {
    return 'destructive';
  }
  return mapVariant(variant);
};

// Map MUI size to shadcn size
const mapSize = (muiSize?: string): ButtonProps['size'] => {
  switch (muiSize) {
    case 'small':
      return 'sm';
    case 'large':
      return 'lg';
    case 'medium':
    default:
      return 'default';
  }
};

export interface ActionType
  extends Omit<ButtonProps, 'key' | 'onClick' | 'variant' | 'size'> {
  key: string;
  label: string;
  'data-testid'?: string;
  disabled?: boolean;
  loading?: boolean;
  startIcon?: React.ReactNode;
  onClick: () => void;
  // Support both MUI and shadcn variants
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link'
    | 'contained'
    | 'outlined'
    | 'text';
  // Support both MUI and shadcn sizes
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'small' | 'medium' | 'large';
  // MUI compatibility
  color?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning';
  sx?: any; // MUI sx prop (ignored but accepted)
}

export interface ActionDialogProps {
  open?: boolean;
  onClose?: () => void;
  title: string;
  description?: string;
  hideDescription?: boolean;
  actions: ActionType[];
  children?: React.ReactNode;
  'data-testid'?: string;
  loading?: boolean;
  showCloseIcon?: boolean;
  maxWidth?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl'
    | 'full';
  highZIndex?: boolean; // Add prop to use higher z-index above dropdowns
  // MUI compatibility props (ignored but accepted)
  fullWidth?: boolean;
  PaperProps?: any;
  sx?: any;
}

const maxWidthClasses = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  '6xl': 'sm:max-w-6xl',
  '7xl': 'sm:max-w-7xl',
  full: 'sm:max-w-[95vw]',
};

const ActionDialog = ({
  open = false,
  onClose,
  title,
  description,
  hideDescription = false,
  actions,
  children,
  loading = false,
  showCloseIcon = true,
  maxWidth = 'lg',
  highZIndex = false,
  'data-testid': testId,
  // Ignored MUI props
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fullWidth,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  PaperProps,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sx,
}: ActionDialogProps) => {
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && onClose && !loading) {
      onClose();
    }
  };

  // Override the DialogContent's built-in close button visibility
  const contentClassName = cn(
    maxWidthClasses[maxWidth],
    'w-full', // Ensure it uses full width up to max
    'gap-6',
    !showCloseIcon && '[&>button[aria-label]]:hidden',
    highZIndex && 'z-[4500]', // Higher than dropdown z-index (4000)
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={contentClassName}
        data-testid={testId}
        onInteractOutside={(e) => {
          if (loading) {
            e.preventDefault();
          }
        }}
        style={highZIndex ? { zIndex: 4500 } : undefined}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>
          {description && (
            <DialogDescription className={hideDescription ? 'sr-only' : ''}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {children && (
          <div className="overflow-y-auto max-h-[70vh] min-h-0 w-full">
            {children}
          </div>
        )}

        {actions.length > 0 && (
          <DialogFooter>
            {actions.map(
              ({
                label,
                key: actionKey,
                loading: buttonLoading,
                disabled,
                startIcon,
                onClick,
                variant = 'outline',
                color,
                size = 'default',
                'data-testid': buttonTestId,
                className,
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                sx: buttonSx,
                ...otherProps
              }) => {
                // Map MUI props to shadcn props
                const mappedVariant = mapColorToVariant(color, variant);
                const mappedSize = mapSize(size);

                return (
                  <Button
                    key={actionKey || label}
                    variant={mappedVariant}
                    size={mappedSize}
                    data-testid={buttonTestId}
                    disabled={disabled || loading || buttonLoading}
                    onClick={onClick}
                    className={cn(className)}
                    {...otherProps}
                  >
                    {(buttonLoading || loading) && (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {!buttonLoading && !loading && startIcon && (
                      <span className="mr-2 flex items-center">
                        {startIcon}
                      </span>
                    )}
                    {label}
                  </Button>
                );
              },
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActionDialog;

// Helper types for specific use cases
export type LabelsType = {
  [key in requestStatusType]: string;
};

export type ActionsType = Pick<
  ActionType,
  'key' | 'onClick' | 'loading' | 'startIcon'
> & {
  labels: LabelsType;
  'data-testid'?: string;
};
